import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DataExportsCard from "./DataExportsCard";
import { 
  Loader2, 
  Upload, 
  Users, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Search,
  LogOut,
  ExternalLink,
  BarChart3,
  BarChart2,
  FileText,
  Download,
  FileDown,
  FileSpreadsheet,
  FolderKanban,
  Calendar,
  Trash2,
  Activity,
  Eye,
  MousePointer,
  AlertTriangle,
  Monitor,
  Smartphone,
  Tablet,
  RefreshCw,
  TrendingUp,
  Timer,
  ChevronLeft,
  ChevronRight,
  Wrench,
  ShieldAlert,
  Zap,
  Code
} from "lucide-react";
import { Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  { value: "all", label: "All Regions" },
  { value: "usa", label: "USA" },
  { value: "canada", label: "Canada" },
  { value: "jamaica", label: "Jamaica" },
  { value: "barbados", label: "Barbados" },
  { value: "trinidad_tobago", label: "Trinidad & Tobago" },
  { value: "panama", label: "Panama" }
];

const ASSESSMENT_TYPES = [
  { value: "all", label: "All Types" },
  { value: "self_assessment", label: "Self Assessment" },
  { value: "technical_assessment", label: "Technical Assessment" },
  { value: "manager_validation", label: "Manager Validation" },
  { value: "hands_on_simulation", label: "Hands-On Simulation" }
];

const REGION_FLAGS = {
  usa: '🇺🇸',
  canada: '🇨🇦',
  jamaica: '🇯🇲',
  barbados: '🇧🇧',
  trinidad_tobago: '🇹🇹',
  panama: '🇵🇦'
};

import ExportDataTab from './ExportDataTab';
import ManagerValidationTab from './ManagerValidationTab';
import TAEmailTab from './TAEmailTab';
import ActivityLogTab from './ActivityLogTab';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [adminInfo, setAdminInfo] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("assessments");
  
  // Project Tracker state
  const [projectTrackerFile, setProjectTrackerFile] = useState(null);
  const [uploadingProjectTracker, setUploadingProjectTracker] = useState(false);
  const [projectTasks, setProjectTasks] = useState([]);
  const projectTrackerInputRef = useRef(null);
  
  // Assessment Templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateFile, setTemplateFile] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const templateInputRef = useRef(null);
  
  // Pagination state for assessments
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsSessions, setAnalyticsSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionActivities, setSessionActivities] = useState([]);
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState("30");
  const [analyticsUserType, setAnalyticsUserType] = useState("all");
  const [analyticsPage, setAnalyticsPage] = useState(1);
  const [analyticsPagination, setAnalyticsPagination] = useState({});
  const [refreshingAnalytics, setRefreshingAnalytics] = useState(false);
  const [analyticsSubTab, setAnalyticsSubTab] = useState("overview");
  const [dropOffData, setDropOffData] = useState(null);
  
  // Support Issues state
  const [supportIssues, setSupportIssues] = useState([]);
  const [loadingSupportIssues, setLoadingSupportIssues] = useState(false);
  const [supportIssueFilter, setSupportIssueFilter] = useState("all");
  const [supportIssuePage, setSupportIssuePage] = useState(1);
  const [supportIssuePagination, setSupportIssuePagination] = useState({ total: 0, totalPages: 1 });
  
  // Maintenance Mode state - per region
  const [maintenanceRegions, setMaintenanceRegions] = useState({
    jamaica: false,
    usa: false,
    canada: false
  });
  const [maintenanceMessage, setMaintenanceMessage] = useState("System update in progress. Please check back shortly.");
  const [togglingMaintenance, setTogglingMaintenance] = useState({});

  useEffect(() => {
    // Support both legacy adminToken and new unified authToken
    const token = localStorage.getItem("adminToken") || localStorage.getItem("authToken");
    const userInfo = localStorage.getItem("userInfo");
    
    if (!token) {
      navigate("/admin/login");
      return;
    }
    
    // If using unified auth, check if user is admin
    if (userInfo && !localStorage.getItem("adminToken")) {
      const user = JSON.parse(userInfo);
      if (user.role !== 'admin') {
        navigate("/login");
        return;
      }
      // Set adminToken for compatibility
      localStorage.setItem("adminToken", token);
    }
    
    fetchData();
  }, [navigate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken") || localStorage.getItem("authToken");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      // Load first 50 assessments for faster initial load
      const [statsRes, assessmentsRes, adminRes, projectRes, maintenanceRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard-stats`, getAuthHeaders()),
        axios.get(`${API}/admin/assessments?page=1&limit=50`, getAuthHeaders()),
        axios.get(`${API}/admin/me`, getAuthHeaders()),
        axios.get(`${API}/admin/project-tracker`, getAuthHeaders()).catch(() => ({ data: { tasks: [] } })),
        axios.get(`${API}/admin/maintenance-mode`, getAuthHeaders()).catch(() => ({ data: { enabled: false } }))
      ]);
      
      setStats(statsRes.data);
      setAssessments(assessmentsRes.data.assessments);
      setPagination(assessmentsRes.data.pagination || { page: 1, limit: 50, total: assessmentsRes.data.assessments.length, totalPages: 1 });
      setAdminInfo(adminRes.data);
      setProjectTasks(projectRes.data.tasks || []);
      
      // Set maintenance regions state
      const maintenanceData = maintenanceRes.data;
      if (maintenanceData.regions) {
        setMaintenanceRegions(maintenanceData.regions);
      }
      if (maintenanceData.message) {
        setMaintenanceMessage(maintenanceData.message);
      }
      
      // Fetch templates separately
      fetchTemplates();
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load dashboard data");
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Maintenance Mode Toggle - per region
  const handleToggleRegionMaintenance = async (region) => {
    try {
      setTogglingMaintenance(prev => ({ ...prev, [region]: true }));
      const newState = !maintenanceRegions[region];
      await axios.post(`${API}/admin/maintenance-mode`, {
        enabled: newState,
        region: region,
        message: maintenanceMessage
      }, getAuthHeaders());
      
      setMaintenanceRegions(prev => ({ ...prev, [region]: newState }));
      const regionLabel = region.charAt(0).toUpperCase() + region.slice(1);
      toast.success(newState 
        ? `Maintenance enabled for ${regionLabel} - assessments blocked` 
        : `Maintenance disabled for ${regionLabel} - assessments accessible`);
    } catch (err) {
      console.error("Error toggling maintenance mode:", err);
      toast.error("Failed to toggle maintenance mode");
    } finally {
      setTogglingMaintenance(prev => ({ ...prev, [region]: false }));
    }
  };
  
  // Toggle all regions at once
  const handleToggleAllMaintenance = async () => {
    try {
      const anyEnabled = Object.values(maintenanceRegions).some(v => v);
      const newState = !anyEnabled;
      
      setTogglingMaintenance({ jamaica: true, usa: true, canada: true });
      
      await axios.post(`${API}/admin/maintenance-mode/regions`, {
        regions: {
          jamaica: newState,
          usa: newState,
          canada: newState
        },
        message: maintenanceMessage
      }, getAuthHeaders());
      
      setMaintenanceRegions({
        jamaica: newState,
        usa: newState,
        canada: newState
      });
      
      toast.success(newState 
        ? "Maintenance enabled for ALL regions" 
        : "Maintenance disabled for ALL regions");
    } catch (err) {
      console.error("Error toggling all maintenance:", err);
      toast.error("Failed to toggle maintenance mode");
    } finally {
      setTogglingMaintenance({});
    }
  };
  
  // Check if any region has maintenance enabled
  const isAnyMaintenanceActive = Object.values(maintenanceRegions).some(v => v);
  
  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const [templatesRes, assignmentsRes] = await Promise.all([
        axios.get(`${API}/dynamic-assessments/templates`, getAuthHeaders()),
        axios.get(`${API}/dynamic-assessments/assignments`, getAuthHeaders())
      ]);
      setTemplates(templatesRes.data.templates || []);
      setAssignments(assignmentsRes.data.assignments || []);
    } catch (err) {
      console.error("Error fetching templates:", err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchAssessments = async (page = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50'
      });
      
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (regionFilter !== "all") params.append("region", regionFilter);
      if (typeFilter !== "all") params.append("assessment_type", typeFilter);
      if (searchTerm.trim()) params.append("search", searchTerm.trim());
      
      const response = await axios.get(`${API}/admin/assessments?${params}`, getAuthHeaders());
      
      if (append) {
        setAssessments(prev => [...prev, ...response.data.assessments]);
      } else {
        setAssessments(response.data.assessments);
      }
      setPagination(response.data.pagination || { page, limit: 50, total: response.data.assessments.length, totalPages: 1 });
      setCurrentPage(page);
    } catch (err) {
      console.error("Error fetching assessments:", err);
      toast.error("Failed to load assessments");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (currentPage < pagination.totalPages && !loadingMore) {
      fetchAssessments(currentPage + 1, true);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard-stats`, getAuthHeaders());
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching dashboard stats:", err);
    }
  };
  
  // Analytics Functions
  const fetchAnalytics = async () => {
    try {
      // Fetch both analytics and drop-off summary in parallel for speed
      const [analyticsRes, dropOffRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard?days=${analyticsTimeRange}`, getAuthHeaders()),
        axios.get(`${API}/analytics/drop-off?summary_only=true`, getAuthHeaders()).catch(() => ({ data: null }))
      ]);
      setAnalytics(analyticsRes.data);
      if (dropOffRes.data) {
        setDropOffData(dropOffRes.data);
      }
    } catch (err) {
      console.error("Error fetching analytics:", err);
    }
  };
  
  const fetchAnalyticsSessions = async () => {
    try {
      const params = new URLSearchParams({
        page: analyticsPage,
        limit: 15,
        days: analyticsTimeRange
      });
      if (analyticsUserType !== "all") {
        params.append("user_type", analyticsUserType);
      }
      const response = await axios.get(`${API}/analytics/sessions?${params}`, getAuthHeaders());
      setAnalyticsSessions(response.data.sessions);
      setAnalyticsPagination(response.data.pagination);
    } catch (err) {
      console.error("Error fetching sessions:", err);
    }
  };
  
  const fetchSessionActivities = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/analytics/sessions/${sessionId}/activities`, getAuthHeaders());
      setSessionActivities(response.data.activities);
    } catch (err) {
      console.error("Error fetching session activities:", err);
    }
  };
  
  // Fetch support issues
  const fetchSupportIssues = async () => {
    setLoadingSupportIssues(true);
    try {
      const params = new URLSearchParams({
        page: supportIssuePage.toString(),
        page_size: '25'
      });
      if (supportIssueFilter !== 'all') {
        params.append('status', supportIssueFilter);
      }
      const response = await axios.get(`${API}/support/issues?${params}`, getAuthHeaders());
      setSupportIssues(response.data.issues);
      setSupportIssuePagination({
        total: response.data.total,
        totalPages: response.data.totalPages
      });
    } catch (err) {
      console.error("Error fetching support issues:", err);
      toast.error("Failed to load support issues");
    } finally {
      setLoadingSupportIssues(false);
    }
  };
  
  // Fetch analytics when tab changes or filters change
  useEffect(() => {
    if (activeTab === "analytics") {
      fetchAnalytics();
      fetchAnalyticsSessions();
    }
  }, [activeTab, analyticsTimeRange]);
  
  useEffect(() => {
    if (activeTab === "analytics") {
      fetchAnalyticsSessions();
    }
  }, [analyticsPage, analyticsUserType]);
  
  // Fetch support issues when tab changes or filters change
  useEffect(() => {
    if (activeTab === "support-issues") {
      fetchSupportIssues();
    }
  }, [activeTab, supportIssueFilter, supportIssuePage]);

  // Fetch assessments when filters change
  useEffect(() => {
    // Only fetch if we have already loaded initial data (not on first mount)
    if (assessments.length > 0 || statusFilter !== "all" || regionFilter !== "all" || typeFilter !== "all") {
      fetchAssessments(1);
    }
  }, [statusFilter, regionFilter, typeFilter]);
  
  const handleRefreshAnalytics = async () => {
    setRefreshingAnalytics(true);
    await fetchAnalytics();
    await fetchAnalyticsSessions();
    setRefreshingAnalytics(false);
    toast.success("Analytics refreshed");
  };
  
  const handleSessionClick = async (session) => {
    setSelectedSession(session);
    await fetchSessionActivities(session.sessionId);
  };
  
  // Analytics helper functions
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return "0s";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };
  
  const formatTimeAgo = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };
  
  // Template Upload Handler
  const handleTemplateUpload = async () => {
    if (!templateFile) {
      toast.error("Please select a DOCX file first");
      return;
    }
    if (!templateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }
    
    try {
      setUploadingTemplate(true);
      const formData = new FormData();
      formData.append('file', templateFile);
      formData.append('name', templateName);
      formData.append('description', templateDescription);
      
      const token = localStorage.getItem("adminToken") || localStorage.getItem("authToken");
      const response = await axios.post(`${API}/dynamic-assessments/templates/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        toast.success(`Template "${response.data.name}" created successfully! ${response.data.sectionCount} sections, ${response.data.questionCount} questions parsed.`);
        setTemplateFile(null);
        setTemplateName("");
        setTemplateDescription("");
        if (templateInputRef.current) templateInputRef.current.value = "";
        fetchTemplates();
      } else {
        toast.error(response.data.message || "Failed to create template");
      }
    } catch (err) {
      console.error("Template upload error:", err);
      toast.error(err.response?.data?.detail || "Failed to upload template");
    } finally {
      setUploadingTemplate(false);
    }
  };

  // Project Tracker Upload Handler
  const handleProjectTrackerUpload = async () => {
    if (!projectTrackerFile) {
      toast.error("Please select a file first");
      return;
    }
    
    try {
      setUploadingProjectTracker(true);
      const formData = new FormData();
      formData.append('file', projectTrackerFile);
      
      const token = localStorage.getItem("adminToken") || localStorage.getItem("authToken");
      const response = await axios.post(`${API}/admin/project-tracker/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(response.data.message || "Project tracker uploaded successfully!");
      setProjectTrackerFile(null);
      if (projectTrackerInputRef.current) {
        projectTrackerInputRef.current.value = '';
      }
      
      // Refresh project tasks
      const projectRes = await axios.get(`${API}/admin/project-tracker`, getAuthHeaders());
      setProjectTasks(projectRes.data.tasks || []);
      
    } catch (err) {
      console.error("Error uploading project tracker:", err);
      toast.error(err.response?.data?.detail || "Failed to upload project tracker");
    } finally {
      setUploadingProjectTracker(false);
    }
  };

  const handleExportPDF = async (candidateId, candidateName) => {
    try {
      toast.loading("Generating PDF...");
      const token = localStorage.getItem("adminToken") || localStorage.getItem("authToken");
      const response = await fetch(`${API}/admin/candidate/${candidateId}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Failed to generate PDF");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Sagicor_Assessment_${candidateName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.dismiss();
      toast.success("PDF downloaded successfully!");
    } catch (err) {
      toast.dismiss();
      toast.error("Failed to export PDF");
      console.error(err);
    }
  };

  const handleDeleteAssessment = async (candidateId, candidateName) => {
    if (!window.confirm(`Are you sure you want to delete the assessment for "${candidateName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/admin/assessment/${candidateId}`, getAuthHeaders());
      if (response.data.success) {
        toast.success(`Assessment for ${candidateName} deleted successfully`);
        // Refresh the assessments list
        fetchAssessments();
        fetchDashboardStats();
      }
    } catch (err) {
      console.error("Error deleting assessment:", err);
      toast.error("Failed to delete assessment");
    }
  };

  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Are you sure you want to delete the template "${templateName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/dynamic-assessments/templates/${templateId}`, getAuthHeaders());
      if (response.data.success) {
        toast.success(`Template "${templateName}" deleted successfully`);
        // Refresh the templates list
        fetchTemplates();
      }
    } catch (err) {
      console.error("Error deleting template:", err);
      toast.error("Failed to delete template");
    }
  };

  const handleExportExcel = async () => {
    try {
      toast.loading("Generating Excel file...");
      const token = localStorage.getItem("adminToken") || localStorage.getItem("authToken");
      
      const response = await fetch(`${API}/admin/assessments/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Failed to generate Excel");
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `Assessments_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
      toast.dismiss();
      toast.success("Excel file downloaded!");
    } catch (err) {
      toast.dismiss();
      toast.error("Failed to export Excel");
      console.error(err);
    }
  };

  const handleExportAllPDF = async () => {
    try {
      toast.loading("Generating summary PDF...");
      const token = localStorage.getItem("adminToken") || localStorage.getItem("authToken");
      let url = `${API}/admin/export-all-assessments`;
      const params = [];
      if (regionFilter !== "all") params.push(`region=${regionFilter}`);
      if (statusFilter !== "all") params.push(`status=${statusFilter}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Failed to generate PDF");
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `Sagicor_Assessment_Summary_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
      toast.dismiss();
      toast.success("Summary PDF downloaded!");
    } catch (err) {
      toast.dismiss();
      toast.error("Failed to export summary");
      console.error(err);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Completed</Badge>;
      case "started":
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">In Progress</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Pending</Badge>;
    }
  };

  const getRegionLabel = (value) => {
    const region = REGIONS.find(r => r.value === value);
    return region ? region.label : value || "—";
  };

  const getTypeLabel = (value) => {
    const type = ASSESSMENT_TYPES.find(t => t.value === value);
    return type ? type.label : value || "—";
  };

  const getAssessmentLink = (assessment) => {
    const base = window.location.origin;
    const isTA = assessment.assessmentType === 'technical_assessment' || assessment.assessmentType === 'hands_on_simulation';
    return isTA
      ? `${base}/ta-assessment/${assessment.uniqueToken}`
      : `${base}/assessment/${assessment.uniqueToken}`;
  };

  const handleExportCSV = () => {
    try {
      toast.loading("Generating CSV...");
      const base = window.location.origin;

      // Group assessments by email to pair TA and HOS
      const byEmail = {};
      assessments.forEach((a) => {
        const email = a.email?.toLowerCase() || "";
        if (!byEmail[email]) byEmail[email] = { name: a.fullName, email: a.email, region: a.region };
        const isTA = a.assessmentType === "technical_assessment" || a.assessmentType === "hands_on_simulation";
        const link = isTA ? `${base}/ta-assessment/${a.uniqueToken}` : `${base}/assessment/${a.uniqueToken}`;
        if (a.assessmentType === "technical_assessment") {
          byEmail[email].ta_id = a.assessmentId;
          byEmail[email].ta_link = link;
          byEmail[email].ta_status = a.status;
          byEmail[email].ta_progress = a.progressPercentage;
          byEmail[email].job_family = a.jobFamily || "";
          byEmail[email].grade_tier = a.gradeTier || "";
        } else if (a.assessmentType === "hands_on_simulation") {
          byEmail[email].hos_id = a.assessmentId;
          byEmail[email].hos_link = link;
          byEmail[email].hos_status = a.status;
          byEmail[email].hos_progress = a.progressPercentage;
          byEmail[email].simulation_mode = a.simulationMode || "";
        } else if (a.assessmentType === "self_assessment") {
          byEmail[email].sa_link = link;
          byEmail[email].sa_status = a.status;
          byEmail[email].sa_progress = a.progressPercentage;
        } else if (a.assessmentType === "manager_validation") {
          byEmail[email].mv_link = link;
          byEmail[email].mv_status = a.status;
          byEmail[email].mv_progress = a.progressPercentage;
        }
      });

      const headers = [
        "Name", "Email", "Region", "Job Family", "Grade Tier",
        "TA ID", "TA Link", "TA Status", "TA Progress",
        "HOS ID", "HOS Link", "HOS Status", "HOS Progress", "Simulation Mode",
        "SA Link", "SA Status", "SA Progress",
        "MV Link", "MV Status", "MV Progress"
      ];

      const escCSV = (val) => {
        const s = String(val ?? "");
        return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const rows = Object.values(byEmail).map((r) => [
        r.name, r.email, r.region, r.job_family, r.grade_tier,
        r.ta_id, r.ta_link, r.ta_status, r.ta_progress,
        r.hos_id, r.hos_link, r.hos_status, r.hos_progress, r.simulation_mode,
        r.sa_link, r.sa_status, r.sa_progress,
        r.mv_link, r.mv_status, r.mv_progress,
      ].map(escCSV).join(","));

      const csv = [headers.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TCA_Assessments_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.dismiss();
      toast.success("CSV downloaded!");
    } catch (err) {
      toast.dismiss();
      toast.error("Failed to export CSV");
      console.error(err);
    }
  };

  const filteredAssessments = assessments.filter(a => {
    const matchesStatus = statusFilter === "all" || a.status === statusFilter;
    const matchesRegion = regionFilter === "all" || a.region === regionFilter;
    const matchesType = typeFilter === "all" || a.assessmentType === typeFilter;
    const matchesSearch = a.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         a.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         a.department?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesRegion && matchesType && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00AEEF] mx-auto mb-3" />
          <p className="text-[#6b7280] text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <header className="sticky-header py-4 px-6 bg-white border-b border-[#e5e7eb]" data-testid="admin-header">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#63C465] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            <div>
              <span className="text-lg font-bold text-[#3b3b3b]">
                Admin Dashboard
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Region Maintenance Toggles */}
            <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${isAnyMaintenanceActive ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
              <Wrench className={`w-4 h-4 ${isAnyMaintenanceActive ? 'text-red-600' : 'text-gray-500'}`} />
              <span className={`text-sm font-medium ${isAnyMaintenanceActive ? 'text-red-700' : 'text-gray-600'}`}>
                Maintenance:
              </span>
              
              {/* Region Maintenance Toggles */}
              {[
                { key: 'jamaica', flag: '🇯🇲' },
                { key: 'usa', flag: '🇺🇸' },
                { key: 'canada', flag: '🇨🇦' },
                { key: 'barbados', flag: '🇧🇧' },
                { key: 'trinidad_tobago', flag: '🇹🇹' },
                { key: 'panama', flag: '🇵🇦' }
              ].map(({ key, flag }) => (
                <div key={key} className="flex items-center gap-1">
                  <span className="text-sm">{flag}</span>
                  <Switch
                    checked={maintenanceRegions[key]}
                    onCheckedChange={() => handleToggleRegionMaintenance(key)}
                    disabled={togglingMaintenance[key]}
                    className={maintenanceRegions[key] ? 'data-[state=checked]:bg-red-600' : ''}
                    data-testid={`maintenance-toggle-${key}`}
                  />
                </div>
              ))}
              
              {/* Toggle All Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToggleAllMaintenance}
                className="text-xs h-6 px-2"
                data-testid="maintenance-toggle-all"
              >
                {isAnyMaintenanceActive ? 'All Off' : 'All On'}
              </Button>
            </div>
            
            <span className="text-sm text-[#6b7280] hidden sm:inline">
              Welcome, {adminInfo?.name}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleLogout}
              className="gap-2 border-[#e5e7eb] text-[#4b5563]"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Maintenance Mode Banner */}
      {/* Maintenance Mode Banner - shows which regions are affected */}
      {isAnyMaintenanceActive && (
        <div className="bg-red-600 text-white py-2 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
            <ShieldAlert className="w-5 h-5" />
            <span className="font-medium">Maintenance Mode Active</span>
            <span className="text-red-200">
              - Blocked regions: 
              {maintenanceRegions.jamaica && ' 🇯🇲 Jamaica'}
              {maintenanceRegions.usa && ' 🇺🇸 USA'}
              {maintenanceRegions.canada && ' 🇨🇦 Canada'}
              {maintenanceRegions.barbados && ' 🇧🇧 Barbados'}
              {maintenanceRegions.trinidad_tobago && ' 🇹🇹 T&T'}
              {maintenanceRegions.panama && ' 🇵🇦 Panama'}
            </span>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto py-8 px-6">
        {/* Data Exports card — JWT-gated download of the THCO private exports.
            Files live under /app/private_exports/ (NOT under any web-served path);
            the backend streams them with Content-Disposition: attachment, so the
            browser saves them with the correct filename. Allowlist-gated:
            joshua@thcohq.com and neil_freyke@sagicor.com only. */}
        <DataExportsCard />

        {/* X-Ray Cohort card (allowlist-gated) — Sagicor brand strip on executive-style card */}
        <div
          data-testid="xray-cohort-card"
          onClick={() => navigate("/admin/xray-cohort")}
          className="mb-6 cursor-pointer transition-shadow hover:shadow-lg"
          style={{
            background: "var(--surface-card)",
            border: "var(--border-width) solid var(--border-default)",
            borderRadius: "1rem",
            boxShadow: "var(--shadow-card)",
            padding: 0,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div style={{ height: 4, background: "linear-gradient(90deg, #00B5EF 0%, #82C341 100%)" }} />
          <div className="flex justify-between items-center" style={{ padding: "20px 24px" }}>
            <div className="flex items-center gap-4">
              <img
                src="/branding/sagicor-logo-stacked.png"
                alt="Sagicor"
                style={{ height: 52, width: "auto" }}
              />
              <div>
                <div className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: "#0099CC" }}>
                  Sub-Phase A/B · Methodology v4.0
                </div>
                <div className="text-2xl font-semibold tracking-tight mt-1" style={{ color: "var(--text-primary)" }}>
                  X-Ray Cohort
                </div>
                <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
                  View generated candidate X-Rays · click through to the full template
                </div>
              </div>
            </div>
            <div
              className="text-xs font-semibold tracking-wide"
              style={{
                padding: "10px 18px",
                background: "linear-gradient(135deg, #00B5EF 0%, #82C341 100%)",
                color: "#FFFFFF",
                borderRadius: 8,
                boxShadow: "0 1px 3px rgba(0,153,204,0.30)",
              }}
            >
              Open Cohort →
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card className="stat-card" data-testid="stat-total">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="stat-label">Total Candidates</p>
                  <p className="stat-value">{stats?.totalCandidates || 0}</p>
                </div>
                <div className="w-12 h-12 bg-[#00AEEF]/10 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#00AEEF]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-pending">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="stat-label">Pending</p>
                  <p className="stat-value text-slate-500">{stats?.pending || 0}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-slate-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-started">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="stat-label">In Progress</p>
                  <p className="stat-value text-[#00AEEF]">{stats?.started || 0}</p>
                </div>
                <div className="w-12 h-12 bg-[#00AEEF]/10 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-[#00AEEF]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-completed">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="stat-label">Completed</p>
                  <p className="stat-value text-[#63C465]">{stats?.completed || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stats?.completionRate || 0}% rate
                  </p>
                </div>
                <Progress value={stats?.completionRate || 0} className="h-2" />
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card cursor-pointer hover:shadow-md transition-shadow" data-testid="stat-manager-validation" onClick={() => setActiveTab('manager-validation')}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="stat-label">Manager Validation</p>
                  <p className="stat-value text-[#8B5CF6]">{stats?.managerValidation?.total || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stats?.managerValidation?.completed || 0} completed · {stats?.managerValidation?.completionRate || 0}% rate
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#8B5CF6]/10 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#8B5CF6]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="flex justify-end mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/ta-dashboard")}
            className="text-[#00AEEF] border-[#00AEEF]/30 hover:bg-[#00AEEF]/10"
            data-testid="ta-dashboard-link"
          >
            <Code className="w-4 h-4 mr-2" /> TA/HOS Dashboard
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="assessments" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <FileText className="w-4 h-4 mr-2" />
              Assessments
            </TabsTrigger>
            <TabsTrigger value="templates" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <Upload className="w-4 h-4 mr-2" />
              Assessment Templates
            </TabsTrigger>
            <TabsTrigger value="project-tracker" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <FolderKanban className="w-4 h-4 mr-2" />
              Project Tracker
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <Activity className="w-4 h-4 mr-2" />
              User Analytics
            </TabsTrigger>
            <TabsTrigger value="support-issues" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <ShieldAlert className="w-4 h-4 mr-2" />
              Support Issues
              {supportIssuePagination.total > 0 && (
                <Badge variant="secondary" className="ml-2 bg-orange-100 text-orange-700">
                  {supportIssuePagination.total}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="export-data" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <Download className="w-4 h-4 mr-2" />
              Export Data
            </TabsTrigger>
            <TabsTrigger value="manager-validation" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <Users className="w-4 h-4 mr-2" />
              Manager Validation
            </TabsTrigger>
            <TabsTrigger value="ta-emails" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <Mail className="w-4 h-4 mr-2" />
              TA/HOS Emails
            </TabsTrigger>
            <TabsTrigger value="activity-log" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <Eye className="w-4 h-4 mr-2" />
              Activity Log
            </TabsTrigger>
          </TabsList>

          {/* Assessments Tab */}
          <TabsContent value="assessments">
            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Link to="/admin/upload">
                <Button 
                  className="bg-[#00AEEF] hover:bg-[#00AEEF]/90 gap-2 w-full sm:w-auto"
                  data-testid="upload-btn"
                >
                  <Upload className="w-4 h-4" />
                  Upload Assessment
                </Button>
              </Link>
              <Link to="/admin/bulk-upload">
                <Button 
                  className="bg-[#003366] hover:bg-[#003366]/90 gap-2 w-full sm:w-auto"
                  data-testid="bulk-upload-btn"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Bulk ZIP Upload
                </Button>
              </Link>
              <Link to="/admin/users">
                <Button 
                  variant="outline"
                  className="gap-2 w-full sm:w-auto"
                  data-testid="manage-users-btn"
                >
                  <Users className="w-4 h-4" />
                  Manage Users
                </Button>
              </Link>
            </div>

            {/* Assessments Table */}
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row gap-4 justify-between">
                    <div>
                      <CardTitle className="text-[#00AEEF]">All Assessments</CardTitle>
                      <CardDescription>
                        Manage and view candidate assessments
                      </CardDescription>
                    </div>
                  </div>
                  
                  {/* Filters Row */}
                  <div className="flex flex-wrap gap-3">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search candidates..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                        data-testid="search-input"
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[140px]" data-testid="status-filter">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="started">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={regionFilter} onValueChange={setRegionFilter}>
                      <SelectTrigger className="w-[160px]" data-testid="region-filter">
                        <SelectValue placeholder="Region" />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIONS.map((region) => (
                          <SelectItem key={region.value} value={region.value}>
                            {region.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger className="w-[180px]" data-testid="type-filter">
                        <SelectValue placeholder="Assessment Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSESSMENT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleExportExcel}
                  className="gap-2"
                  data-testid="export-excel-btn"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Export Excel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCSV}
                  className="gap-2"
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleExportAllPDF}
                  className="gap-2"
                  data-testid="export-all-btn"
                >
                  <FileDown className="w-4 h-4" />
                  Export Summary
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredAssessments.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No assessments found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Upload DOCX files to create candidate assessments
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>TCA ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Region</TableHead>
                      <TableHead>Assessment Type</TableHead>
                      <TableHead>Assessment Link</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAssessments.map((assessment) => (
                      <TableRow key={assessment.id} data-testid={`assessment-row-${assessment.id}`}>
                        <TableCell>
                          <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                            {assessment.assessmentId || assessment.uniqueToken?.slice(0, 10) || "N/A"}
                          </code>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{assessment.fullName}</p>
                            <p className="text-sm text-muted-foreground">{assessment.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{REGION_FLAGS[assessment.region] || '🌍'}</span>
                            <span className="text-sm">{getRegionLabel(assessment.region)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{getTypeLabel(assessment.assessmentType)}</span>
                        </TableCell>
                        <TableCell>
                          {assessment.uniqueToken ? (
                            <div className="flex items-center gap-1">
                              <code
                                className="text-[11px] bg-slate-50 border px-1.5 py-0.5 rounded max-w-[180px] truncate block cursor-pointer hover:bg-slate-100 transition-colors"
                                title={getAssessmentLink(assessment)}
                                onClick={async () => {
                                  const link = getAssessmentLink(assessment);
                                  try {
                                    await navigator.clipboard.writeText(link);
                                    toast.success("Link copied!");
                                  } catch {
                                    toast.error("Failed to copy");
                                  }
                                }}
                                data-testid={`assessment-link-${assessment.id}`}
                              >
                                {getAssessmentLink(assessment)}
                              </code>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(assessment.status)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={assessment.progressPercentage} className="w-16 h-2" />
                            <span className="text-sm">{assessment.progressPercentage}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Link to={`/admin/candidate/${assessment.id}`}>
                              <Button variant="outline" size="sm" data-testid={`view-btn-${assessment.id}`}>
                                View
                              </Button>
                            </Link>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleExportPDF(assessment.id, assessment.fullName)}
                              data-testid={`export-pdf-btn-${assessment.id}`}
                              title="Download PDF"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                const link = `${window.location.origin}/assessment/${assessment.uniqueToken}`;
                                try {
                                  await navigator.clipboard.writeText(link);
                                  toast.success("Assessment link copied to clipboard!");
                                } catch (err) {
                                  // Fallback for browsers that don't support clipboard API
                                  const textArea = document.createElement('textarea');
                                  textArea.value = link;
                                  textArea.style.position = 'fixed';
                                  textArea.style.left = '-999999px';
                                  document.body.appendChild(textArea);
                                  textArea.select();
                                  try {
                                    document.execCommand('copy');
                                    toast.success("Assessment link copied!");
                                  } catch (e) {
                                    toast.error("Failed to copy link");
                                  }
                                  document.body.removeChild(textArea);
                                }
                              }}
                              data-testid={`copy-link-btn-${assessment.id}`}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAssessment(assessment.id, assessment.fullName)}
                              data-testid={`delete-btn-${assessment.id}`}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Delete Assessment"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            
            {/* Load More Button - shown when there are assessments */}
            {filteredAssessments.length > 0 && pagination.total > filteredAssessments.length && (
              <div className="mt-4 flex flex-col items-center gap-2 border-t pt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {filteredAssessments.length} of {pagination.total} assessments
                </p>
                {currentPage < pagination.totalPages && (
                  <Button
                    variant="outline"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="w-full max-w-xs"
                    data-testid="load-more-btn"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        Load More ({pagination.total - filteredAssessments.length} remaining)
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Assessment Templates Tab */}
      <TabsContent value="templates">
        <div className="space-y-6">
          {/* Upload New Template Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#00AEEF]">Upload Assessment Template</CardTitle>
              <CardDescription>
                Upload a DOCX file to create a new assessment template. The document structure will be preserved exactly for candidates to fill out.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#374151]">Template Name *</label>
                  <Input
                    type="text"
                    placeholder="e.g., Sagicor Self-Assessment 2026"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="h-10"
                    data-testid="template-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#374151]">Description (Optional)</label>
                  <Input
                    type="text"
                    placeholder="Brief description of this assessment"
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    className="h-10"
                    data-testid="template-description-input"
                  />
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <div className="flex-1">
                  <input
                    type="file"
                    accept=".docx"
                    ref={templateInputRef}
                    onChange={(e) => setTemplateFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#00AEEF]/10 file:text-[#00AEEF] hover:file:bg-[#00AEEF]/20"
                    data-testid="template-file-input"
                  />
                  {templateFile && (
                    <p className="mt-2 text-sm text-[#63C465]">Selected: {templateFile.name}</p>
                  )}
                </div>
                <Button
                  onClick={handleTemplateUpload}
                  disabled={uploadingTemplate || !templateFile || !templateName.trim()}
                  className="bg-[#63C465] hover:bg-[#4fa651] gap-2"
                  data-testid="upload-template-btn"
                >
                  {uploadingTemplate ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Create Template
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Existing Templates */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#00AEEF]">Assessment Templates</CardTitle>
              <CardDescription>
                {templates.length} template{templates.length !== 1 ? 's' : ''} available
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingTemplates ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-[#00AEEF]" />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p>No templates uploaded yet</p>
                  <p className="text-sm">Upload a DOCX file above to create your first assessment template</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Template Name</TableHead>
                      <TableHead>Sections</TableHead>
                      <TableHead>Assignments</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {templates.map((template) => (
                      <TableRow key={template.id}>
                        <TableCell>
                          <div className="font-medium text-[#3b3b3b]">{template.name}</div>
                          {template.description && (
                            <div className="text-xs text-muted-foreground">{template.description}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-[#00AEEF]/10 text-[#00AEEF]">
                            {template.sectionCount} sections
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-[#3b3b3b]">{template.assignmentCount}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {template.createdAt ? new Date(template.createdAt).toLocaleDateString() : 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(`/dynamic-assessment/preview/${template.id}`, '_blank')}
                              className="text-[#00AEEF] border-[#00AEEF]/30 hover:bg-[#00AEEF]/10"
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Preview
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTemplate(template.id, template.name)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Delete Template"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Assignments Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#00AEEF]">Assigned Assessments</CardTitle>
              <CardDescription>
                Track candidate progress on assigned assessments
              </CardDescription>
            </CardHeader>
            <CardContent>
              {assignments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p>No assessments assigned yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Candidate</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Assigned</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignments.map((assignment) => (
                      <TableRow key={assignment.id}>
                        <TableCell>
                          <div className="font-medium text-[#3b3b3b]">{assignment.candidate?.fullName}</div>
                          <div className="text-xs text-muted-foreground">{assignment.candidate?.email}</div>
                        </TableCell>
                        <TableCell>
                          <span className="text-[#3b3b3b]">{assignment.template?.name}</span>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline"
                            className={
                              assignment.status === 'completed' 
                                ? 'bg-[#63C465]/10 text-[#63C465] border-[#63C465]/30'
                                : assignment.status === 'started'
                                ? 'bg-[#00AEEF]/10 text-[#00AEEF] border-[#00AEEF]/30'
                                : 'bg-gray-100 text-gray-600'
                            }
                          >
                            {assignment.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={assignment.progressPercentage} className="w-16 h-2" />
                            <span className="text-sm text-[#3b3b3b]">{assignment.progressPercentage}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {assignment.assignedAt ? new Date(assignment.assignedAt).toLocaleDateString() : 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {assignment.status === 'completed' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const response = await axios.get(
                                    `${API}/dynamic-assessments/assignments/${assignment.id}/download`,
                                    getAuthHeaders()
                                  );
                                  // Create a downloadable JSON file
                                  const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `assessment_${assignment.candidate?.fullName?.replace(/\s+/g, '_')}_${Date.now()}.json`;
                                  a.click();
                                  URL.revokeObjectURL(url);
                                  toast.success("Assessment downloaded successfully");
                                } catch (err) {
                                  toast.error("Failed to download assessment");
                                }
                              }}
                              className="text-[#63C465] border-[#63C465]/30 hover:bg-[#63C465]/10"
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Download
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      {/* Project Tracker Tab */}
      <TabsContent value="project-tracker">
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <div>
                  <CardTitle className="text-[#00AEEF]">Project Tracker</CardTitle>
                  <CardDescription>
                    Upload and manage project tracking files
                  </CardDescription>
                </div>
              </div>
              
              {/* Upload Section */}
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <div className="flex-1">
                  <input
                    ref={projectTrackerInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => setProjectTrackerFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#00AEEF]/10 file:text-[#00AEEF] hover:file:bg-[#00AEEF]/20"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Upload Excel or CSV files for project tracking
                  </p>
                </div>
                <Button
                  onClick={handleProjectTrackerUpload}
                  disabled={!projectTrackerFile || uploadingProjectTracker}
                  className="bg-[#00AEEF] hover:bg-[#00AEEF]/90 gap-2"
                >
                  {uploadingProjectTracker ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Upload
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {projectTasks.length === 0 ? (
              <div className="text-center py-12">
                <FolderKanban className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No project tasks found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Upload a project tracking file to view tasks
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Workstream</TableHead>
                      <TableHead>Task</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Region</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Target Date</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Priority</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projectTasks.map((task, index) => (
                      <TableRow key={task.id || index}>
                        <TableCell>
                          <span className="text-xs font-medium text-[#00AEEF]">
                            {task.workstream || "General"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium text-[#3b3b3b]">{task.task || task.task_name || "Unnamed Task"}</p>
                          {task.dependencies && (
                            <p className="text-xs text-[#9ca3af] mt-0.5">Depends: {task.dependencies}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-[#3b3b3b]">{task.owner || "Unassigned"}</p>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-[#3b3b3b]">{task.region || "All"}</span>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            className={
                              task.status === 'Complete' 
                                ? "bg-[#63C465]/10 text-[#4fa651] hover:bg-[#63C465]/10"
                                : task.status === 'In Progress'
                                ? "bg-[#00AEEF]/10 text-[#00AEEF] hover:bg-[#00AEEF]/10"
                                : "bg-slate-100 text-slate-700 hover:bg-slate-100"
                            }
                          >
                            {task.status || "Not Started"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-[#9ca3af]" />
                            <span className="text-sm text-[#3b3b3b]">
                              {task.targetDate ? new Date(task.targetDate).toLocaleDateString() : "No date"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={task.percentComplete || 0} className="w-16 h-2" />
                            <span className="text-xs text-[#3b3b3b]">{task.percentComplete || 0}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline"
                            className={
                              task.priority === 'High' || task.priority === 'Critical'
                                ? "border-red-200 text-red-700"
                                : task.priority === 'Medium'
                                ? "border-[#00AEEF]/30 text-[#00AEEF]"
                                : "border-[#63C465]/30 text-[#4fa651]"
                            }
                          >
                            {task.priority || "Low"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Analytics Tab */}
      <TabsContent value="analytics">
        {/* Analytics Header */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
          <div className="flex items-center gap-3">
            <Select value={analyticsTimeRange} onValueChange={setAnalyticsTimeRange}>
              <SelectTrigger className="w-[140px]" data-testid="analytics-time-range">
                <SelectValue placeholder="Time Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefreshAnalytics}
              disabled={refreshingAnalytics}
              className="gap-2"
              data-testid="refresh-analytics-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshingAnalytics ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          <Link to="/admin/analytics">
            <Button 
              className="bg-[#00AEEF] hover:bg-[#00AEEF]/90 gap-2"
              data-testid="full-analytics-btn"
            >
              <BarChart2 className="w-4 h-4" />
              Full Analytics Dashboard
            </Button>
          </Link>
        </div>

        {/* Analytics Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-sessions">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#00AEEF]/10 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#00AEEF]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#3b3b3b]">{analytics?.summary?.totalSessions || 0}</p>
                  <p className="text-xs text-[#6b7280]">Total Sessions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-active">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#63C465]/10 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-[#63C465]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#63C465]">{analytics?.summary?.activeSessions || 0}</p>
                  <p className="text-xs text-[#6b7280]">Active Now</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-duration">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#3b3b3b]">
                    {dropOffData?.summary?.avgActiveHours 
                      ? `${dropOffData.summary.avgActiveHours.toFixed(1)}h`
                      : formatDuration(analytics?.summary?.avgSessionDuration || 0)
                    }
                  </p>
                  <p className="text-xs text-[#6b7280]">Avg Active Time</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-pageviews">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#3b3b3b]">{analytics?.summary?.totalPageViews || 0}</p>
                  <p className="text-xs text-[#6b7280]">Page Views</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-actions">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <MousePointer className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#3b3b3b]">{analytics?.summary?.totalActions || 0}</p>
                  <p className="text-xs text-[#6b7280]">Actions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-errors">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{analytics?.summary?.totalErrors || 0}</p>
                  <p className="text-xs text-[#6b7280]">Errors</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Analytics Sub-tabs */}
        <Tabs value={analyticsSubTab} onValueChange={setAnalyticsSubTab} className="w-full">
          <TabsList className="mb-4 bg-white">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="sessions" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <Users className="w-4 h-4 mr-2" />
              User Sessions
            </TabsTrigger>
            <TabsTrigger value="errors" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Errors
            </TabsTrigger>
          </TabsList>

          {/* Overview Sub-tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Sessions by User Type */}
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#00AEEF]" />
                    Sessions by User Type
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics?.sessionsByType && Object.keys(analytics.sessionsByType).length > 0 ? (
                      Object.entries(analytics.sessionsByType).map(([type, count]) => {
                        const total = Object.values(analytics.sessionsByType).reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                        return (
                          <div key={type} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-[#3b3b3b] capitalize">{type || 'Unknown'}</span>
                              <span className="text-[#6b7280]">{count} ({percentage}%)</span>
                            </div>
                            <Progress value={percentage} className="h-2" />
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8 text-[#6b7280]">No session data yet</div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Sessions by Device */}
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                    <Monitor className="w-5 h-5 text-[#00AEEF]" />
                    Sessions by Device
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    {['desktop', 'mobile', 'tablet'].map(device => {
                      const count = analytics?.sessionsByDevice?.[device] || 0;
                      const Icon = device === 'desktop' ? Monitor : device === 'mobile' ? Smartphone : Tablet;
                      return (
                        <div key={device} className="text-center p-4 bg-[#f8f9fa] rounded-lg">
                          <Icon className="w-8 h-8 mx-auto mb-2 text-[#00AEEF]" />
                          <p className="text-2xl font-bold text-[#3b3b3b]">{count}</p>
                          <p className="text-xs text-[#6b7280] capitalize">{device}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Top Pages */}
              <Card className="bg-white border-0 shadow-sm lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                    <Eye className="w-5 h-5 text-[#00AEEF]" />
                    Top Pages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.topPages && analytics.topPages.length > 0 ? (
                    <div className="space-y-2">
                      {analytics.topPages.slice(0, 5).map((page, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-[#f8f9fa] rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#3b3b3b] truncate">
                              {page.title || page.url || 'Unknown Page'}
                            </p>
                            <p className="text-xs text-[#6b7280] truncate">{page.url}</p>
                          </div>
                          <div className="text-right ml-4">
                            <p className="text-sm font-bold text-[#00AEEF]">{page.views} views</p>
                            <p className="text-xs text-[#6b7280]">{formatDuration(page.avgDuration)} avg</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-[#6b7280]">No page view data yet</div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Sessions Sub-tab */}
          <TabsContent value="sessions">
            <Card className="bg-white border-0 shadow-sm">
              <CardHeader>
                <div className="flex flex-col sm:flex-row gap-4 justify-between">
                  <div>
                    <CardTitle className="text-[#3b3b3b]">User Sessions</CardTitle>
                    <CardDescription>Detailed view of all user sessions</CardDescription>
                  </div>
                  <Select value={analyticsUserType} onValueChange={(v) => { setAnalyticsUserType(v); setAnalyticsPage(1); }}>
                    <SelectTrigger className="w-[160px]" data-testid="user-type-filter">
                      <SelectValue placeholder="User Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      <SelectItem value="candidate">Candidates</SelectItem>
                      <SelectItem value="admin">Admins</SelectItem>
                      <SelectItem value="anonymous">Anonymous</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {analyticsSessions.length === 0 ? (
                  <div className="text-center py-12 text-[#6b7280]">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-40" />
                    <p>No sessions found</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>User</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Device</TableHead>
                            <TableHead>Duration</TableHead>
                            <TableHead>Pages</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Started</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {analyticsSessions.map((session) => (
                            <TableRow 
                              key={session.id} 
                              className={`cursor-pointer hover:bg-[#f8f9fa] ${selectedSession?.id === session.id ? 'bg-[#00AEEF]/5' : ''}`}
                              onClick={() => handleSessionClick(session)}
                              data-testid={`session-row-${session.id}`}
                            >
                              <TableCell>
                                <div>
                                  <p className="font-medium text-[#3b3b3b]">{session.userName}</p>
                                  {session.userEmail && (
                                    <p className="text-xs text-[#6b7280]">{session.userEmail}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="capitalize">{session.userType}</Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  {session.deviceType === 'mobile' ? (
                                    <Smartphone className="w-4 h-4 text-[#6b7280]" />
                                  ) : session.deviceType === 'tablet' ? (
                                    <Tablet className="w-4 h-4 text-[#6b7280]" />
                                  ) : (
                                    <Monitor className="w-4 h-4 text-[#6b7280]" />
                                  )}
                                  <span className="text-xs text-[#6b7280]">{session.browser || 'Unknown'}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="font-medium">{formatDuration(session.totalDuration)}</span>
                              </TableCell>
                              <TableCell>
                                <span className="text-[#00AEEF] font-medium">{session.pageViews}</span>
                              </TableCell>
                              <TableCell>
                                {session.isActive ? (
                                  <Badge className="bg-[#63C465]/10 text-[#63C465] hover:bg-[#63C465]/10">Active</Badge>
                                ) : session.endedAt ? (
                                  <Badge variant="outline" className="text-[#6b7280]">Ended</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-orange-600 border-orange-200">Idle</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-[#6b7280]">{formatTimeAgo(session.startedAt)}</span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Pagination */}
                    {analyticsPagination.totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <p className="text-sm text-[#6b7280]">
                          Page {analyticsPagination.page} of {analyticsPagination.totalPages}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setAnalyticsPage(p => Math.max(1, p - 1))}
                            disabled={analyticsPage === 1}
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setAnalyticsPage(p => Math.min(analyticsPagination.totalPages, p + 1))}
                            disabled={analyticsPage === analyticsPagination.totalPages}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Session Detail */}
            {selectedSession && (
              <Card className="bg-white border-0 shadow-sm mt-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-[#3b3b3b]">Session Details</CardTitle>
                      <CardDescription>{selectedSession.userName} - {formatTimeAgo(selectedSession.startedAt)}</CardDescription>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedSession(null)}>Close</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    <div className="p-3 bg-[#f8f9fa] rounded-lg">
                      <p className="text-xs text-[#6b7280]">Duration</p>
                      <p className="font-bold text-[#3b3b3b]">{formatDuration(selectedSession.totalDuration)}</p>
                    </div>
                    <div className="p-3 bg-[#f8f9fa] rounded-lg">
                      <p className="text-xs text-[#6b7280]">Page Views</p>
                      <p className="font-bold text-[#00AEEF]">{selectedSession.pageViews}</p>
                    </div>
                    <div className="p-3 bg-[#f8f9fa] rounded-lg">
                      <p className="text-xs text-[#6b7280]">Actions</p>
                      <p className="font-bold text-[#3b3b3b]">{selectedSession.actionsCount}</p>
                    </div>
                    <div className="p-3 bg-[#f8f9fa] rounded-lg">
                      <p className="text-xs text-[#6b7280]">Errors</p>
                      <p className={`font-bold ${selectedSession.errorsCount > 0 ? 'text-red-600' : 'text-[#63C465]'}`}>
                        {selectedSession.errorsCount}
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-[#3b3b3b] mb-3">Activity Timeline</h4>
                    {sessionActivities.length === 0 ? (
                      <p className="text-sm text-[#6b7280]">No detailed activities recorded</p>
                    ) : (
                      <div className="space-y-2 max-h-[250px] overflow-y-auto">
                        {sessionActivities.map((activity, index) => (
                          <div key={activity.id || index} className="flex items-start gap-3 p-2 hover:bg-[#f8f9fa] rounded">
                            <div className={`w-2 h-2 mt-2 rounded-full ${
                              activity.eventType === 'error' ? 'bg-red-500' :
                              activity.eventType === 'action' ? 'bg-[#00AEEF]' :
                              'bg-[#63C465]'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-[#3b3b3b]">{activity.eventName}</span>
                                <Badge variant="outline" className="text-xs">{activity.eventType}</Badge>
                              </div>
                              {activity.pageUrl && (
                                <p className="text-xs text-[#6b7280] truncate">{activity.pageUrl}</p>
                              )}
                            </div>
                            <span className="text-xs text-[#6b7280] whitespace-nowrap">
                              {formatTimeAgo(activity.createdAt)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Errors Sub-tab */}
          <TabsContent value="errors">
            <Card className="bg-white border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Recent Errors & Issues
                </CardTitle>
                <CardDescription>Track and resolve user-facing errors</CardDescription>
              </CardHeader>
              <CardContent>
                {analytics?.recentErrors && analytics.recentErrors.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.recentErrors.map((error, index) => (
                      <div key={error.id || index} className="p-4 bg-red-50 border border-red-100 rounded-lg">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                              <span className="font-medium text-red-700">{error.errorMessage || 'Unknown Error'}</span>
                            </div>
                            <p className="text-sm text-[#6b7280]">
                              User: {error.userName} ({error.userEmail || 'No email'})
                            </p>
                            {error.pageUrl && (
                              <p className="text-xs text-[#6b7280] truncate">Page: {error.pageUrl}</p>
                            )}
                          </div>
                          <span className="text-xs text-[#6b7280] whitespace-nowrap">
                            {formatTimeAgo(error.createdAt)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-[#63C465]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-[#63C465]" />
                    </div>
                    <p className="text-[#63C465] font-medium">No errors recorded!</p>
                    <p className="text-sm text-[#6b7280] mt-1">The platform is running smoothly</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </TabsContent>
      
      {/* Support Issues Tab */}
      <TabsContent value="support-issues">
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 text-orange-500" />
                  Support Issues Log
                </CardTitle>
                <CardDescription>
                  Issues reported by internal team - diagnose in staging, deploy manually to production
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Select value={supportIssueFilter} onValueChange={(v) => { setSupportIssueFilter(v); setSupportIssuePage(1); }}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="reported">Reported</SelectItem>
                    <SelectItem value="diagnosing">Diagnosing</SelectItem>
                    <SelectItem value="fixing">Fixing</SelectItem>
                    <SelectItem value="fixed">Fixed</SelectItem>
                    <SelectItem value="deployed">Deployed</SelectItem>
                    <SelectItem value="escalated">Escalated</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={fetchSupportIssues} disabled={loadingSupportIssues}>
                  <RefreshCw className={`w-4 h-4 ${loadingSupportIssues ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loadingSupportIssues ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#00AEEF]" />
              </div>
            ) : supportIssues.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ShieldAlert className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No support issues found</p>
                <p className="text-sm mt-1">Issues reported by internal team will appear here</p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Issue ID</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Reported By</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {supportIssues.map((issue) => (
                      <TableRow key={issue.id}>
                        <TableCell className="font-mono text-xs">
                          #{issue.id.substring(0, 8)}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{issue.employeeName}</div>
                            <div className="text-xs text-gray-500">{issue.employeeEmail}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {issue.issueCategory?.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            issue.status === 'deployed' || issue.status === 'verified' || issue.status === 'closed' 
                              ? 'bg-green-100 text-green-800' 
                              : issue.status === 'escalated' 
                                ? 'bg-red-100 text-red-800'
                                : issue.status === 'diagnosing' || issue.status === 'fixing'
                                  ? 'bg-blue-100 text-blue-800'
                                  : issue.status === 'fixed'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                          }>
                            {issue.status === 'diagnosing' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                            {issue.status === 'deployed' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                            {issue.status === 'escalated' && <AlertTriangle className="w-3 h-3 mr-1" />}
                            {issue.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {issue.reportedBy}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {new Date(issue.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => window.open(`/support/issues/${issue.id}`, '_blank')}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Pagination */}
                {supportIssuePagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-600">
                      Page {supportIssuePage} of {supportIssuePagination.totalPages} ({supportIssuePagination.total} issues)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSupportIssuePage(p => Math.max(1, p - 1))}
                        disabled={supportIssuePage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSupportIssuePage(p => Math.min(supportIssuePagination.totalPages, p + 1))}
                        disabled={supportIssuePage === supportIssuePagination.totalPages}
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
        
        {/* Legend/Info Card */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Issue Status Guide</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Badge className="bg-gray-100 text-gray-800">Reported</Badge>
                <span className="text-gray-600">New issue</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-800">Diagnosing</Badge>
                <span className="text-gray-600">Auto-analyzing</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-yellow-100 text-yellow-800">Fixed</Badge>
                <span className="text-gray-600">Ready for deploy</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-green-100 text-green-800">Deployed</Badge>
                <span className="text-gray-600">Live in staging</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-red-100 text-red-800">Escalated</Badge>
                <span className="text-gray-600">Needs manual fix</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-green-200 text-green-900">Verified</Badge>
                <span className="text-gray-600">Confirmed working</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="export-data">
        <ExportDataTab />
      </TabsContent>

      <TabsContent value="manager-validation">
        <ManagerValidationTab />
      </TabsContent>

      <TabsContent value="ta-emails">
        <TAEmailTab token={localStorage.getItem("adminToken") || localStorage.getItem("authToken") || ""} />
      </TabsContent>

      <TabsContent value="activity-log">
        <ActivityLogTab token={localStorage.getItem("adminToken") || localStorage.getItem("authToken") || ""} />
      </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
